import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";

interface DropdownOption {
    id: string;
    primaryText: string;
    secondaryText?: string;
}

interface CustomDropdownProps {
    options: DropdownOption[];
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    className?: string;
    required?: boolean;
    disabled?: boolean;
    modalTitle?: string; // New prop for modal title
}

export const CustomDropdown: React.FC<CustomDropdownProps> = ({
    options,
    value,
    onChange,
    placeholder = "Select an option",
    className = "",
    required = false,
    disabled = false,
    modalTitle = "Select an option" // Default modal title
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const triggerRef = useRef<HTMLButtonElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    // Find the selected option
    const selectedOption = options.find(option => option.id === value);

    // Filter options based on search term
    const filteredOptions = options.filter(option =>
        option.primaryText.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (option.secondaryText && option.secondaryText.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    // Focus search input when modal opens
    useEffect(() => {
        if (isOpen && searchInputRef.current) {
            setTimeout(() => {
                searchInputRef.current?.focus();
            }, 100);
        }
    }, [isOpen]);

    // Reset search when closing
    useEffect(() => {
        if (!isOpen) {
            setSearchTerm("");
        }
    }, [isOpen]);

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (
                isOpen &&
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen]);

    // Close dropdown on escape key
    useEffect(() => {
        function handleEscapeKey(event: KeyboardEvent) {
            if (event.key === 'Escape' && isOpen) {
                setIsOpen(false);
            }
        }

        document.addEventListener("keydown", handleEscapeKey);
        return () => {
            document.removeEventListener("keydown", handleEscapeKey);
        };
    }, [isOpen]);

    return (
        <div className={`relative ${className}`}>
            <button
                ref={triggerRef}
                type="button"
                onClick={() => !disabled && setIsOpen(!isOpen)}
                className={`block w-full text-left border border-gray-300 rounded-md shadow-sm py-2 px-3 
                    focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 
                    dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm
                    ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                    ${required && !value ? 'border-red-500' : ''}`}
                aria-haspopup="dialog"
                aria-expanded={isOpen}
                disabled={disabled}
            >
                {selectedOption ? (
                    <span className="truncate block">{selectedOption.primaryText}</span>
                ) : (
                    <span className="text-gray-400 truncate block">{placeholder}</span>
                )}
                <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                    <svg
                        className="h-5 w-5 text-gray-400"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                    >
                        <path
                            fillRule="evenodd"
                            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                            clipRule="evenodd"
                        />
                    </svg>
                </span>
            </button>

            {isOpen && ReactDOM.createPortal(
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div
                        ref={dropdownRef}
                        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="dropdown-modal-title"
                    >
                        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                            <h3 id="dropdown-modal-title" className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                                {modalTitle}
                            </h3>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <svg
                                        className="h-5 w-5 text-gray-400"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                </div>
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                    placeholder="Search..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="overflow-y-auto flex-1 p-1">
                            {filteredOptions.length === 0 ? (
                                <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                                    No options match your search
                                </div>
                            ) : (
                                <div className="py-1">
                                    {/* Clear selection option */}
                                    <div
                                        className="cursor-pointer select-none relative py-2 pl-3 pr-9 text-gray-900 dark:text-white hover:bg-indigo-600 hover:text-white rounded-md mx-1"
                                        onClick={() => {
                                            onChange("");
                                            setIsOpen(false);
                                        }}
                                        role="option"
                                    >
                                        {placeholder}
                                    </div>

                                    {filteredOptions.map((option) => (
                                        <div
                                            key={option.id}
                                            className={`cursor-pointer select-none relative py-2 pl-3 pr-9 rounded-md mx-1
                                                ${value === option.id
                                                    ? 'bg-indigo-100 dark:bg-indigo-900 text-indigo-900 dark:text-indigo-100'
                                                    : 'text-gray-900 dark:text-white hover:bg-indigo-600 hover:text-white'}`}
                                            onClick={() => {
                                                onChange(option.id);
                                                setIsOpen(false);
                                            }}
                                            role="option"
                                            aria-selected={value === option.id}
                                        >
                                            <div className="font-medium truncate">{option.primaryText}</div>
                                            {option.secondaryText && (
                                                <div className={`text-xs truncate ${value === option.id
                                                    ? 'text-indigo-700 dark:text-indigo-300'
                                                    : 'text-gray-500 dark:text-gray-400 group-hover:text-white'}`}>
                                                    {option.secondaryText}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="p-3 border-t border-gray-200 dark:border-gray-700 flex justify-end">
                            <button
                                type="button"
                                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-600"
                                onClick={() => setIsOpen(false)}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};
